import React, { useEffect, useRef, useState } from 'react';
import { Download, Receipt } from 'lucide-react';
import { consultarFacturaReserva, descargarPDFFactura } from '../../../../services/facturacionService';
import { useTheme } from '../../../../context/ThemeContext';

type FacturaDisponible = { id: number; numero_factura: number; estado: string };

export default function FacturaCliente({ citaId }: { citaId: number }) {
    const { isDark } = useTheme();
    const [resultado, setResultado] = useState<{ citaId: number; factura: FacturaDisponible } | null>(null);
    const [descargando, setDescargando] = useState<'carta' | 'rollo' | null>(null);
    const [error, setError] = useState('');
    const bloqueo = useRef(false);
    useEffect(() => {
        let vigente = true;
        setResultado(null);
        setError('');
        consultarFacturaReserva(citaId).then(data => {
            if (!vigente) return;
            const facturas: FacturaDisponible[] = Array.isArray(data) ? data : data.results || [];
            const factura = facturas.find(item => ['validada', 'anulada'].includes(item.estado));
            if (factura) setResultado({ citaId, factura });
        }).catch(() => { if (vigente) setResultado(null); });
        return () => { vigente = false; };
    }, [citaId]);
    const factura = resultado?.citaId === citaId ? resultado.factura : null;
    if (!factura) return null;
    const descargar = async (formato: 'carta' | 'rollo') => {
        if (bloqueo.current) return;
        bloqueo.current = true;
        setDescargando(formato); setError('');
        try { await descargarPDFFactura(factura.id, formato); }
        catch (e: any) { setError(e?.message || 'No se pudo descargar la factura. Intenta nuevamente.'); }
        finally { bloqueo.current = false; setDescargando(null); }
    };
    return <section aria-label="Factura de tu reserva" style={{ padding: 24, borderRadius: 18, background: isDark ? '#160a2b' : '#fff', border: '1px solid rgba(97,37,123,.18)', color: isDark ? '#fff' : '#451759' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Receipt size={24} aria-hidden="true"/>
            <h3 style={{ fontSize: 17, fontWeight: 800 }}>Factura N° {factura.numero_factura}</h3>
        </div>
        <p style={{ fontSize: 13, margin: '10px 0 16px', color: isDark ? '#cbd5e1' : '#64748b' }}>Descarga tu factura en PDF en el formato que prefieras.</p>
        {factura.estado === 'anulada' && <p style={{ color: isDark ? '#fda4af' : '#be123c', fontWeight: 700, marginBottom: 12 }}>Factura anulada · Copia para consulta</p>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {(['carta', 'rollo'] as const).map(formato => <button key={formato} type="button" disabled={!!descargando} onClick={() => descargar(formato)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 16px', borderRadius: 12, border: '1px solid #61257B', background: formato === 'carta' ? '#61257B' : 'transparent', color: formato === 'carta' || isDark ? '#fff' : '#61257B', fontWeight: 700, cursor: descargando ? 'wait' : 'pointer', opacity: descargando ? .65 : 1 }}>
                <Download size={16} aria-hidden="true"/>{descargando === formato ? 'Descargando…' : formato === 'carta' ? 'Descargar carta' : 'Descargar rollo (80 mm)'}
            </button>)}
        </div>
        {error && <p role="alert" style={{ color: isDark ? '#fda4af' : '#be123c', marginTop: 12, fontSize: 13 }}>{error}</p>}
    </section>;
}
